import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  LogIn, LogOut, RefreshCcw, Search, FileText, Phone, Mail, MapPin, BedDouble, Calendar,
  AlertTriangle, CheckCircle2, ExternalLink, ChevronDown, Users, Wallet,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { it as itLocale } from "date-fns/locale";
import { errMsg } from "@/lib/errors";

const _str = (v) => (typeof v === "string" ? v : v?.it || v?.en || (v && Object.values(v)[0]) || "");

const SOURCE_LABEL = {
  walk_in: "Walk-in (Guida)",
  guide_webhook: "Guida Digitale",
  guide_webhook_replay: "Guida (replay)",
  website: "Sito web",
  external: "Esterno (Booking/Airbnb)",
  manual: "Aggiunto manualmente",
};
const SOURCE_TONE = {
  walk_in: "bg-amber-100 text-amber-800 border-amber-300",
  guide_webhook: "bg-amber-100 text-amber-800 border-amber-300",
  guide_webhook_replay: "bg-amber-50 text-amber-700 border-amber-200",
  website: "bg-emerald-100 text-emerald-800 border-emerald-300",
  external: "bg-sky-100 text-sky-800 border-sky-300",
  manual: "bg-slate-100 text-slate-700 border-slate-300",
};

export default function CheckinsTab() {
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [filterPeriod, setFilterPeriod] = useState(() => {
    // Pick up "checkins_filter" query param (set by dashboard cards that
    // deep-link into this tab with a pre-applied filter).
    try {
      const p = new URLSearchParams(window.location.search);
      const v = p.get("checkins_filter");
      if (v && ["all", "today", "today_expected", "today_done", "in_house", "past_7", "past_30", "upcoming", "needs_room"].includes(v)) {
        // Clean the URL so refreshing doesn't re-apply
        const url = new URL(window.location.href);
        url.searchParams.delete("checkins_filter");
        window.history.replaceState({}, "", url.toString());
        return v;
      }
    } catch (e) { /* noop */ }
    return "all";
  });
  const [assignModal, setAssignModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, r] = await Promise.allSettled([
        api.get("/admin/bookings"),
        api.get("/rooms"),
      ]);
      if (b.status === "fulfilled") {
        setBookings(b.value.data || []);
      } else {
        toast.error("Errore caricamento check-in");
        // eslint-disable-next-line no-console
        console.error("/admin/bookings failed:", b.reason);
      }
      if (r.status === "fulfilled") {
        setRooms(r.value.data || []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  // Filter: this tab tracks REAL CHECK-INS performed via the App Guida.
  // We include only records that have actually been processed:
  //   - status = "checked_in"  (ospite attualmente in casa)
  //   - status = "completed"   (check-in fatto + check-out fatto)
  //   - source = "walk_in"     (ingressi diretti tramite App Guida)
  // Future/upcoming confirmed bookings are NOT included here — those live
  // in the "Prenotazioni" tab. This avoids cluttering Check-in with rows
  // that the host hasn't actually processed yet via the guide app.
  const checkins = useMemo(() => {
    const rows = bookings.filter((b) => {
      if (b.status === "checked_in") return true;
      if (b.status === "completed") return true;
      if (b.source === "walk_in") return true;
      return false;
    });
    // Apply source filter
    let filtered = rows;
    if (filterSource !== "all") {
      filtered = filtered.filter((b) => (b.source || "manual") === filterSource);
    }
    // Apply period filter
    if (filterPeriod !== "all") {
      const now = new Date();
      const todayISO = format(now, "yyyy-MM-dd");
      const inDays = (n) => {
        const d = new Date(now); d.setDate(d.getDate() + n);
        return format(d, "yyyy-MM-dd");
      };
      const ago = (n) => {
        const d = new Date(now); d.setDate(d.getDate() - n);
        return format(d, "yyyy-MM-dd");
      };
      if (filterPeriod === "today") filtered = filtered.filter((b) => b.check_in === todayISO);
      else if (filterPeriod === "today_expected") {
        // Arrivi previsti oggi NON ancora arrivati fisicamente
        filtered = filtered.filter((b) =>
          b.check_in === todayISO
          && b.status !== "checked_in"
          && b.status !== "cancelled"
          && b.status !== "no_show"
          && b.status !== "rejected"
          && b.status !== "completed"
        );
      }
      else if (filterPeriod === "today_done") {
        // Check-in effettivamente registrati oggi
        filtered = filtered.filter((b) => {
          if (b.status !== "checked_in") return false;
          const actual = (b.actual_checkin_at || "").slice(0, 10);
          return actual === todayISO || b.check_in === todayISO;
        });
      }
      else if (filterPeriod === "in_house") filtered = filtered.filter((b) => {
        if (!b.check_in || !b.check_out) return false;
        if (b.check_in > todayISO || b.check_out < todayISO) return false;
        // "In casa adesso" = ospite FISICAMENTE entrato (status checked_in).
        // NON includere "confirmed" — quello è solo una prenotazione confermata,
        // l'ospite potrebbe non essere ancora arrivato.
        return b.status === "checked_in";
      });
      else if (filterPeriod === "past_7") filtered = filtered.filter((b) => b.check_in >= ago(7) && b.check_in <= todayISO);
      else if (filterPeriod === "past_30") filtered = filtered.filter((b) => b.check_in >= ago(30) && b.check_in <= todayISO);
      else if (filterPeriod === "upcoming") filtered = filtered.filter((b) => b.check_in > todayISO && b.check_in <= inDays(30));
      else if (filterPeriod === "needs_room") filtered = filtered.filter((b) => b.needs_room_assignment || (!b.room_id && b.guide_room_number == null));
    }
    // Apply search
    if (search.trim()) {
      const s = search.toLowerCase();
      filtered = filtered.filter((b) => {
        const g = b.guest || {};
        return [g.first_name, g.last_name, g.email, g.phone, g.document_number, b.code, _str(b.room_name)]
          .filter(Boolean).join(" ").toLowerCase().includes(s);
      });
    }
    // Sort: most recent check-in first
    return filtered.sort((a, b) => (b.check_in || "").localeCompare(a.check_in || ""));
  }, [bookings, filterSource, filterPeriod, search]);

  // Stats for header chips
  const stats = useMemo(() => {
    const todayISO = format(new Date(), "yyyy-MM-dd");
    const walkin_pending = bookings.filter((b) => b.needs_room_assignment).length;
    // ARRIVI ATTESI OGGI = prenotazioni con arrivo oggi, NON ancora arrivate
    // fisicamente (status !== checked_in). Serve per organizzarsi il pomeriggio.
    const today_expected = bookings.filter((b) =>
      b.check_in === todayISO
      && b.status !== "checked_in"
      && b.status !== "cancelled"
      && b.status !== "no_show"
      && b.status !== "rejected"
      && b.status !== "completed"
    ).length;
    // CHECK-IN OGGI = ospiti che hanno fisicamente fatto check-in oggi
    // (independentemente dalla data di arrivo nella prenotazione, perché si
    // potrebbero essere registrati a mano in ritardo).
    const today_done = bookings.filter((b) => {
      if (b.status !== "checked_in") return false;
      // Match on check_in date (the calendar arrival) OR on actual_checkin_at if present
      const actual = (b.actual_checkin_at || "").slice(0, 10);
      return actual === todayISO || b.check_in === todayISO;
    }).length;
    // Manteniamo per compat il vecchio `today_count` come somma (attesi + arrivati oggi)
    const today_count = today_expected + today_done;
    // "In casa adesso" = ospiti FISICAMENTE in struttura ora.
    // Solo prenotazioni con `status === "checked_in"`.
    const in_house = bookings.filter((b) => {
      if (!b.check_in || !b.check_out) return false;
      if (b.check_in > todayISO || b.check_out < todayISO) return false;
      return b.status === "checked_in";
    }).length;
    return { walkin_pending, today_count, today_expected, today_done, in_house, total: checkins.length };
  }, [bookings, checkins.length]);

  const openAssign = async (b) => {
    setAssignModal({
      ...b,
      _selected_room_id: "",
      _selected_room_name: "",
      _selected_room_number: "",
      _price_per_night: "",
      _guests: b.guests || 1,
      _nights: b.nights || 1,
    });
  };

  const markCompleted = async (b) => {
    try {
      // Use the dedicated checkout endpoint that also pushes the event to the
      // guide app (bidirectional sync).
      await api.post(`/admin/bookings/${b.id}/checkout`);
      toast.success(`Check-out registrato: ${b.guest?.first_name || ""} ${b.guest?.last_name || ""}`.trim());
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Errore aggiornamento status"));
    }
  };

  const submitAssign = async () => {
    if (!assignModal?._selected_room_id) {
      toast.error("Seleziona una camera");
      return;
    }
    try {
      await api.post(`/admin/bookings/${assignModal.id}/assign-room`, {
        room_id: assignModal._selected_room_id,
        room_name: assignModal._selected_room_name || undefined,
        guide_room_number: assignModal._selected_room_number || undefined,
        price_per_night: parseFloat(assignModal._price_per_night || 0) || 0,
        guests: parseInt(assignModal._guests, 10) || 1,
        nights: parseInt(assignModal._nights, 10) || 1,
      });
      toast.success(`Camera ${assignModal._selected_room_number ? `#${assignModal._selected_room_number}` : ""} assegnata`.trim());
      setAssignModal(null);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Errore assegnazione"));
    }
  };

  return (
    <div className="space-y-4" data-testid="checkins-tab">
      {/* HEADER */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
              <LogIn className="w-5 h-5" />
              Check-in
            </h3>
            <p className="text-xs text-[#635F59] mt-1">
              Tutti gli ospiti che hanno fatto check-in: dal sito, walk-in dalla Guida Digitale, o registrati a mano. Sincronizzato in tempo reale.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/bookings/auto-complete-stale");
                  if (data?.ok) {
                    const n = data.updated || 0;
                    if (n > 0) {
                      toast.success(`Pulite ${n} prenotazioni scadute (status → completed)`);
                      await load();
                    } else {
                      toast.info("Nessuna prenotazione scaduta da pulire");
                    }
                  } else {
                    toast.error(data?.error || "Errore pulizia");
                  }
                } catch (e) {
                  toast.error(errMsg(e, "Errore pulizia"));
                }
              }}
              variant="outline"
              size="sm"
              className="text-amber-700 border-amber-300 hover:bg-amber-50"
              title="Marca come 'completed' tutte le prenotazioni ancora in stato checked-in il cui check-out è già passato"
              data-testid="checkins-auto-complete"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Pulisci scadute
            </Button>
            <Button onClick={load} variant="outline" data-testid="checkins-refresh">
              <RefreshCcw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Aggiorna
            </Button>
          </div>
        </div>
        {/* Stat chips — cliccabili per applicare il filtro corrispondente */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mt-4">
          <StatChip
            label="Attesi oggi"
            value={stats.today_expected}
            icon={Calendar}
            tone="emerald"
            active={filterPeriod === "today_expected"}
            onClick={() => setFilterPeriod(filterPeriod === "today_expected" ? "all" : "today_expected")}
            testid="stat-today-expected"
          />
          <StatChip
            label="Check-in oggi"
            value={stats.today_done}
            icon={CheckCircle2}
            tone="teal"
            active={filterPeriod === "today_done"}
            onClick={() => setFilterPeriod(filterPeriod === "today_done" ? "all" : "today_done")}
            testid="stat-today-done"
          />
          <StatChip
            label="In casa adesso"
            value={stats.in_house}
            icon={BedDouble}
            tone="blue"
            active={filterPeriod === "in_house"}
            onClick={() => setFilterPeriod(filterPeriod === "in_house" ? "all" : "in_house")}
            testid="stat-in-house"
          />
          <StatChip
            label="Senza camera assegnata"
            value={stats.walkin_pending}
            icon={AlertTriangle}
            tone="amber"
            active={filterPeriod === "needs_room"}
            onClick={() => setFilterPeriod(filterPeriod === "needs_room" ? "all" : "needs_room")}
            testid="stat-needs-room"
          />
          <StatChip
            label="Visibili (filtrati)"
            value={stats.total}
            icon={CheckCircle2}
            tone="slate"
            active={filterPeriod === "all" && filterSource === "all" && !search}
            onClick={() => { setFilterPeriod("all"); setFilterSource("all"); setSearch(""); }}
            testid="stat-reset-filters"
          />
        </div>
      </div>

      {/* FILTERS */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#635F59]" />
          <Input
            placeholder="Cerca nome, codice, documento, camera…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            data-testid="checkins-search"
          />
        </div>
        <Select value={filterPeriod} onValueChange={setFilterPeriod}>
          <SelectTrigger className="w-[160px]" data-testid="checkins-filter-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i periodi</SelectItem>
            <SelectItem value="today">Solo oggi (qualsiasi)</SelectItem>
            <SelectItem value="today_expected">⏳ Arrivi attesi oggi</SelectItem>
            <SelectItem value="today_done">✓ Check-in fatti oggi</SelectItem>
            <SelectItem value="in_house">In casa adesso</SelectItem>
            <SelectItem value="past_7">Ultimi 7 giorni</SelectItem>
            <SelectItem value="past_30">Ultimi 30 giorni</SelectItem>
            <SelectItem value="upcoming">Prossimi 30 giorni</SelectItem>
            <SelectItem value="needs_room">🛏️ Senza camera assegnata</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="w-[200px]" data-testid="checkins-filter-source">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le origini</SelectItem>
            <SelectItem value="walk_in">Walk-in (Guida Digitale)</SelectItem>
            <SelectItem value="website">Sito web</SelectItem>
            <SelectItem value="external">Esterno (Booking/Airbnb)</SelectItem>
            <SelectItem value="manual">Aggiunto manualmente</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* LIST */}
      <div className="space-y-2">
        {loading && checkins.length === 0 ? (
          <div className="bg-white border border-[#E8E2D9] rounded-xl p-12 text-center text-[#635F59]">
            <RefreshCcw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Caricamento…
          </div>
        ) : checkins.length === 0 ? (
          <div className="bg-white border border-[#E8E2D9] rounded-xl p-12 text-center text-[#635F59]" data-testid="checkins-empty">
            <LogIn className="w-12 h-12 mx-auto mb-3 text-[#C9C2B6]" />
            <h4 className="font-semibold text-[#2D2926] mb-1">Nessun check-in trovato</h4>
            <p className="text-sm">Modifica i filtri o attendi che arrivi un nuovo check-in dall'app Guida.</p>
          </div>
        ) : (
          checkins.map((b) => <CheckinRow key={b.id} b={b} onAssign={openAssign} onMarkCompleted={markCompleted} />)
        )}
      </div>

      {/* ASSIGN ROOM MODAL */}
      {assignModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
          onClick={(e) => { if (e.target === e.currentTarget) setAssignModal(null); }}
          data-testid="checkin-assign-modal"
        >
          <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-2xl">
            <h3 className="font-display text-xl text-[#1E3A5F] font-semibold mb-1">Assegna camera & tariffa</h3>
            <p className="text-xs text-[#635F59] mb-4">
              Ospite: <strong>{[assignModal.guest?.first_name, assignModal.guest?.last_name].filter(Boolean).join(" ") || "—"}</strong> · {assignModal.check_in} → {assignModal.check_out}
            </p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Camera fisica * <span className="text-[10px] text-[#635F59] font-normal">(scegli numero specifico)</span></Label>
                <select
                  value={assignModal._selected_room_number ? `${assignModal._selected_room_id}|${assignModal._selected_room_number}` : (assignModal._selected_room_id || "")}
                  onChange={(e) => {
                    // Value is either "<roomId>" (category-only) or "<roomId>|<physicalNumber>"
                    const raw = e.target.value;
                    const [rid, num] = raw.split("|");
                    const room = rooms.find((rr) => rr.id === rid);
                    const rname = room ? _str(room.name) : "";
                    setAssignModal({
                      ...assignModal,
                      _selected_room_id: rid || "",
                      _selected_room_name: rname,
                      _selected_room_number: num || "",
                      // Pre-fill price with the room's nightly rate so the admin
                      // sees a sensible default — they can override it manually
                      // for walk-ins with custom pricing.
                      _price_per_night: room?.price_per_night || "",
                    });
                  }}
                  data-testid="checkin-assign-room-select"
                  className="mt-1 w-full h-10 border border-[#E8E2D9] rounded-md px-3 text-sm bg-white"
                >
                  <option value="">— Seleziona camera —</option>
                  {rooms.map((r) => {
                    const nums = Array.isArray(r.guide_room_numbers) ? r.guide_room_numbers : [];
                    const name = _str(r.name);
                    if (nums.length === 0) {
                      return (
                        <option key={r.id} value={r.id}>
                          {name}
                        </option>
                      );
                    }
                    return (
                      <optgroup key={r.id} label={name}>
                        {nums.map((n) => (
                          <option key={`${r.id}-${n}`} value={`${r.id}|${n}`}>
                            #{n} · {name}
                          </option>
                        ))}
                        <option value={r.id}>(qualunque {name})</option>
                      </optgroup>
                    );
                  })}
                </select>
                <p className="text-[10px] text-[#635F59] mt-1">
                  Es. <code>#11 Camera Matrimoniale</code> = camera fisica 11. Se non sai il numero esatto, scegli &quot;(qualunque)&quot;.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Notti</Label>
                  <Input
                    type="number" min="1"
                    value={assignModal._nights}
                    onChange={(e) => setAssignModal({ ...assignModal, _nights: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Ospiti</Label>
                  <Input
                    type="number" min="1"
                    value={assignModal._guests}
                    onChange={(e) => setAssignModal({ ...assignModal, _guests: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">€/notte</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={assignModal._price_per_night}
                    onChange={(e) => setAssignModal({ ...assignModal, _price_per_night: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="bg-[#FAF7F2] rounded p-2 text-xs text-[#2D2926]">
                Totale stimato: <strong>€{((parseFloat(assignModal._price_per_night || 0) || 0) * (parseInt(assignModal._nights, 10) || 0)).toFixed(2)}</strong>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setAssignModal(null)}
                className="px-3 py-1.5 text-sm text-[#635F59] hover:text-[#2D2926] rounded"
              >
                Annulla
              </button>
              <Button
                onClick={submitAssign}
                disabled={!assignModal._selected_room_id}
                className="bg-[#AC5D45] hover:bg-[#9A5240] text-white font-semibold"
                data-testid="checkin-assign-confirm"
              >
                Conferma
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatChip({ label, value, icon: Icon, tone, active = false, onClick, testid }) {
  const tones = {
    emerald: {
      base: "bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-50",
      active: "bg-emerald-600 border-emerald-600 text-white",
    },
    blue: {
      base: "bg-white border-blue-300 text-blue-700 hover:bg-blue-50",
      active: "bg-blue-600 border-blue-600 text-white",
    },
    amber: {
      base: "bg-white border-amber-300 text-amber-700 hover:bg-amber-50",
      active: "bg-amber-500 border-amber-500 text-white",
    },
    slate: {
      base: "bg-white border-slate-300 text-slate-700 hover:bg-slate-50",
      active: "bg-slate-700 border-slate-700 text-white",
    },
    teal: {
      base: "bg-white border-teal-300 text-teal-700 hover:bg-teal-50",
      active: "bg-teal-600 border-teal-600 text-white",
    },
  };
  const cls = active ? tones[tone].active : tones[tone].base;
  const isClickable = typeof onClick === "function";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isClickable}
      data-testid={testid || `checkin-stat-${tone}`}
      aria-pressed={active}
      title={isClickable ? (active ? "Clicca per rimuovere il filtro" : `Filtra: ${label}`) : undefined}
      className={`border rounded-md p-3 text-left transition ${cls} ${isClickable ? "cursor-pointer active:scale-[0.98]" : "cursor-default"}`}
    >
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 shrink-0" />
        <span className="text-[10px] uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <div className="text-2xl font-bold mt-1 leading-none">{value}</div>
    </button>
  );
}

function CheckinRow({ b, onAssign, onMarkCompleted }) {
  const [open, setOpen] = useState(false);
  const g = b.guest || {};
  const name = [g.first_name, g.last_name].filter(Boolean).join(" ") || "Ospite senza nome";
  // Pre-compute derived guest strings once per render (avoids running filter/join
  // inside JSX which would re-create the arrays even when the section is hidden).
  const provenienza = [g.city, g.country].filter(Boolean).join(", ");
  const documento = [g.document, g.document_number].filter(Boolean).join(" · ");
  const src = b.source || "manual";
  const needsAssign = b.needs_room_assignment === true;
  const roomLabel = _str(b.room_name) || "—";
  const checkinDate = b.check_in ? format(parseISO(b.check_in), "dd/MM/yyyy") : "—";
  const checkoutDate = b.check_out ? format(parseISO(b.check_out), "dd/MM/yyyy") : "—";
  // "Mark as left" button: shown whenever the guest is still "in_house"
  // (checked_in or confirmed), regardless of the planned check-out date.
  // The host might check the guest out earlier (early departure) and we
  // sync that to the App Guida.
  const canMarkCompleted = (
    (b.status === "checked_in" || b.status === "confirmed") &&
    !needsAssign
  );

  return (
    <div className={`bg-white border rounded-xl overflow-hidden ${needsAssign ? "border-amber-300" : "border-[#E8E2D9]"}`} data-testid={`checkin-row-${b.code}`}>
      <div
        className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-[#FAF7F2]"
        onClick={() => setOpen((o) => !o)}
      >
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${needsAssign ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
          {needsAssign ? <AlertTriangle className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[#1E3A5F] truncate">{name}</span>
            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${SOURCE_TONE[src] || SOURCE_TONE.manual}`}>
              {SOURCE_LABEL[src] || src}
            </span>
            <span className="text-[11px] font-mono text-[#635F59]">{b.code}</span>
          </div>
          {needsAssign && (
            <div className="mt-1">
              <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-600 text-white inline-flex items-center gap-1">
                <AlertTriangle className="w-2.5 h-2.5" /> Camera da assegnare
              </span>
            </div>
          )}
          <div className="text-xs text-[#635F59] mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {checkinDate} → {checkoutDate}</span>
            <span className="inline-flex items-center gap-1">
              <BedDouble className="w-3 h-3" />
              {roomLabel}
              {b.guide_room_number != null && (
                <span className="ml-0.5 font-mono font-bold text-white bg-[#1E3A5F] px-1 rounded text-[10px]" title="Numero camera (App Guida)">#{b.guide_room_number}</span>
              )}
            </span>
            <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {b.guests || 1}</span>
            {b.total > 0 && <span className="inline-flex items-center gap-1"><Wallet className="w-3 h-3" /> €{b.total.toFixed(2)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {needsAssign && (
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); onAssign(b); }}
              className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold"
              data-testid={`checkin-assign-btn-${b.code}`}
            >
              Assegna camera
            </Button>
          )}
          {b.status === "completed" ? (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-full px-2 py-1"
              title={`Check-out completato${b.checkout_source === "guide" ? " dall'App Guida" : b.checkout_source === "pms" ? " manualmente" : ""}${b.guest_checked_out_at ? ` il ${b.guest_checked_out_at.slice(0,10)}` : ""}`}
              data-testid={`checkin-checkout-done-${b.code}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Check-out fatto
            </span>
          ) : canMarkCompleted ? (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); onMarkCompleted && onMarkCompleted(b); }}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs font-semibold"
              title="Marca questa prenotazione come terminata (sincronizza anche con l'App Guida)"
              data-testid={`checkin-mark-completed-${b.code}`}
            >
              <LogOut className="w-3.5 h-3.5 mr-1" />
              Check-out
            </Button>
          ) : null}
          <ChevronDown className={`w-4 h-4 text-[#635F59] transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 pt-2 border-t border-[#E8E2D9] grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs bg-[#FDFBF7]">
          <InfoLine icon={Mail} label="Email" value={g.email} />
          <InfoLine icon={Phone} label="Telefono" value={g.phone} />
          <InfoLine icon={MapPin} label="Provenienza" value={provenienza} />
          <InfoLine icon={Calendar} label="Data di nascita" value={g.date_of_birth} />
          <InfoLine icon={FileText} label="Documento" value={documento} />
          <InfoLine icon={ExternalLink} label="Nazionalità" value={g.nationality} />
        </div>
      )}
    </div>
  );
}

function InfoLine({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 mt-0.5 text-[#635F59] shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[#635F59] font-semibold">{label}</div>
        <div className="text-[#2D2926] break-words">{value || <span className="text-[#A8A6A2] italic">—</span>}</div>
      </div>
    </div>
  );
}
